import _ from 'lodash';

export function getIXPreviewStyles(state) {
  const styles = getStyleJSON(state).reduce((result, {data}) => {
    const {sel, styleText} = data;
    result.push(`${sel} { ${styleText} }`);
    return result;
  }, []);
  return styles.length ? styles.join('\n') : null;
}

export function buildTransformBody(transform = {}) {
  const {keysort} = transform;
  if (!keysort) return null;

  const {
    x,
    y,
    z,
    scaleX,
    scaleY,
    scaleZ,
    rotateX,
    rotateY,
    rotateZ,
    skewX,
    skewY,
  } = transform;

  const translate = () => {
    if (x != null && y != null && z != null) {
      if (z === '0px') {
        return 'translate(' + x + ',' + y + ')';
      } else {
        return 'translate3d(' + x + ',' + y + ',' + z + ')';
      }
    }
  };

  const scale = () => {
    if (scaleX != null && scaleY != null && scaleZ != null) {
      if (scaleZ === 1) {
        return 'scale(' + scaleX + ',' + scaleY + ')';
      } else {
        return 'scale3d(' + scaleX + ',' + scaleY + ',' + scaleZ + ')';
      }
    }
  };

  const rotate = () => {
    if (rotateX != null && rotateY != null && rotateZ != null) {
      if (rotateX === '0deg' && rotateY === '0deg') {
        return 'rotate(' + rotateZ + ')';
      } else {
        return (
          'rotateX(' +
          rotateX +
          ') rotateY(' +
          rotateY +
          ') rotateZ(' +
          rotateZ +
          ')'
        );
      }
    }
  };

  const skew = () => {
    if (skewX != null && skewY != null) {
      return 'skewX(' + skewX + ') skewY(' + skewY + ')';
    }
  };

  return keysort
    .reduce((result, key) => {
      let value;
      switch (key) {
        case 'x': {
          value = translate();
          break;
        }
        case 'scaleX': {
          value = scale();
          break;
        }
        case 'rotateX': {
          value = rotate();
          break;
        }
        case 'skewX': {
          value = skew();
          break;
        }
      }
      if (value) {
        result.push(value);
      }
      return result;
    }, [])
    .join(' ');
}

export function buildStyleBody(style) {
  if (!style) return;

  const out = [];
  const {display, opacity, width, height} = style;
  const transforms = buildTransformBody(style);

  if (display) out.push('display:' + display);
  if (opacity != null) out.push('opacity:' + opacity);
  if (width != null) out.push('width:' + width);
  if (height != null) out.push('height:' + height);
  if (transforms && transforms.length) {
    out.push('transform:' + transforms);
  }

  return out.join(';');
}

export function getStyleJSON({macros}) {
  return macros.reduce((result, macro) => {
    const {_id, slug, value} = macro;

    // Make sure the persisted CSS value is valid, since the publishing pipeline takes it verbatim
    let styleText = buildStyleBody(value.style);
    if (styleText && styleText.length && !_.endsWith(styleText, ';')) {
      styleText += ';';
    }

    if (!styleText) {
      return result;
    }

    const data = {
      sel: getIXStyleSelector(slug),
      styleText,
    };

    result.push({
      _id,
      macro: true,
      data,
    });

    return result;
  }, []);
}

function getIXStyleSelector(slug) {
  return `html.w-mod-js *[data-ix="${slug}"]`;
}

export function getMacrosWith3dProps(sourceMacros) {
  return sourceMacros.update((macros) => {
    let newMacros = macros;
    const triggersPath = ['value', 'triggers'];
    macros.forEach((macro) => {
      const triggers = macro.getIn(triggersPath);
      const styleHas3d = has3dTransformValue(macro.getIn(['value', 'style']));
      triggers.forEach((trigger, triggerIndex) => {
        const {selector} = trigger;

        // When a selector exists, relax 3d check to look for props, otherwise look for props w/ nonzero values.
        // NOTE: this makes it posisble for selector-based triggers to add unnecessary 3d transform properties, but
        // this is a necessary tradeoff as we are unable to access the proxy node's IX styles prior to runtime.
        const stepWith3d = selector
          ? has3dTransformProperty
          : has3dTransformValue;

        // Inherit 3d from style as long as the trigger does not have a selector
        const inheritStyle3d = styleHas3d && !selector;

        const found3d = Boolean(
          inheritStyle3d ||
          trigger.stepsA.find(stepWith3d) ||
          trigger.stepsB.find(stepWith3d)
        );
        if (found3d || trigger.preserve3d) {
          const macroTriggerPath = [macro._id].concat(
            triggersPath,
            triggerIndex
          );
          const newTrigger = trigger.set('preserve3d', found3d);
          newMacros = newMacros.setIn(macroTriggerPath, newTrigger);
        }
      });
    });
    return newMacros;
  });
}

const zeroValueStepRegex = /^0[a-z]*$/;
export function has3dTransformValue({
  z = null,
  rotateX = null,
  rotateY = null,
  scaleZ = null,
}) {
  return (
    (z !== null && !zeroValueStepRegex.test(z)) ||
    (rotateX !== null && !zeroValueStepRegex.test(rotateX)) ||
    (rotateY !== null && !zeroValueStepRegex.test(rotateY)) ||
    (scaleZ !== null && scaleZ !== 1)
  );
}

export function has3dTransformProperty({
  z = null,
  rotateX = null,
  rotateY = null,
  scaleZ = null,
}) {
  return z !== null || rotateX !== null || rotateY !== null || scaleZ !== null;
}
